/**
 * Canonical activity-tag definitions. Mirrors the Worker's
 * ACTIVITY_KEYS exactly — every key listed here must exist there
 * (and vice versa), otherwise the chip won't survive a save.
 *
 * Each entry carries:
 *   - key: the canonical string stored in trail_activities /
 *     organization_activities. Used as the form value AND the
 *     network payload.
 *   - label: what the user reads.
 *   - sfSymbol: the matching SF Symbol name on iOS so both
 *     surfaces feel consistent when we ship the iOS chip in a
 *     follow-up.
 *   - icon: a stroke-based inline SVG matching the AMENITIES
 *     icon family (single-color, 24×24, currentColor stroke).
 *
 * Adding a new activity:
 *   1. Add it to ACTIVITY_KEYS in worker/src/catalog-shared.ts
 *   2. Append an entry here
 *   3. (Later) Add a matching SF Symbol mapping on iOS
 *
 * No DB migration needed for new keys — the join tables store
 * TEXT, validation is at the Worker boundary via isActivityKey.
 */

import type { ReactNode } from "react";

export type ActivityKey =
  | "hiking"
  | "cycling"
  | "mountain_biking"
  | "gravel"
  | "horseback"
  | "ohv";

export interface ActivityDef {
  key: ActivityKey;
  label: string;
  sfSymbol: string;
  icon: ReactNode;
}

const STROKE = "currentColor";
const stroke = {
  fill: "none",
  stroke: STROKE,
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export const ACTIVITIES: ActivityDef[] = [
  {
    key: "hiking",
    label: "Hiking",
    sfSymbol: "figure.hiking",
    icon: (
      // Hiker silhouette: round head, body with a pack, raised
      // leg suggesting motion, walking stick.
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="13" cy="4.5" r="1.6" />
        <path d="M12 7l-2 5 3 2 1 6" />
        <path d="M13 14l3-1 2 3" />
        <path d="M9 21l1-5" />
        <path d="M7 12l-1 9" />
      </svg>
    ),
  },
  {
    key: "cycling",
    label: "Cycling",
    sfSymbol: "bicycle",
    icon: (
      // Classic bicycle — two wheels, frame triangle, handlebars +
      // saddle. Thin tires read as "road/path bike."
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="5.5" cy="16.5" r="3.5" />
        <circle cx="18.5" cy="16.5" r="3.5" />
        <path d="M5.5 16.5l4-7h6l3 7" />
        <path d="M9.5 9.5l3 7" />
        <path d="M14 6h2.5l1 3.5" />
      </svg>
    ),
  },
  {
    key: "mountain_biking",
    label: "Mountain Biking",
    sfSymbol: "figure.outdoor.cycle",
    icon: (
      // Mountain bike + leaning rider — fatter tires implied by the
      // double-stroke wheel hint, frame more aggressively angled.
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="5" cy="17" r="3" />
        <circle cx="19" cy="17" r="3" />
        <path d="M5 17l5-6 4 2 5 4" />
        <path d="M10 11l2-4h3" />
        <circle cx="14.5" cy="5" r="1.4" />
        <path d="M2.5 17.5h1M20.5 17.5h1" />
      </svg>
    ),
  },
  {
    key: "gravel",
    label: "Gravel",
    sfSymbol: "bicycle",
    icon: (
      // Gravel bike — same silhouette as road, with stippled
      // ground beneath to suggest unpaved surface.
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="6" cy="15" r="3" />
        <circle cx="18" cy="15" r="3" />
        <path d="M6 15l4-6h5l3 6" />
        <path d="M10 9l3 6" />
        <path d="M14 6h2l1 3" />
        <circle cx="3" cy="21" r="0.6" fill={STROKE} stroke="none" />
        <circle cx="8" cy="21.5" r="0.6" fill={STROKE} stroke="none" />
        <circle cx="13" cy="21" r="0.6" fill={STROKE} stroke="none" />
        <circle cx="18" cy="21.5" r="0.6" fill={STROKE} stroke="none" />
        <circle cx="21" cy="21" r="0.6" fill={STROKE} stroke="none" />
      </svg>
    ),
  },
  {
    key: "horseback",
    label: "Horseback",
    sfSymbol: "figure.equestrian.sports",
    icon: (
      // Horse silhouette — body, neck arching up to a head with
      // a small ear, four legs.
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M3 16c2-4 5-5 8-5l3-3 2 1-1 2c2 1 4 3 5 6" />
        <path d="M16 6.5l1-1.5" />
        <path d="M5 16v4M9 17v4M14 17v4M19 17v4" />
      </svg>
    ),
  },
  {
    key: "ohv",
    label: "OHV",
    sfSymbol: "motorcycle",
    icon: (
      // Quad / ATV — four wheels (rendered as two on each side),
      // body with handlebars and a seat.
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="5" cy="17" r="2.5" />
        <circle cx="19" cy="17" r="2.5" />
        <path d="M5 17h3l2-4h6l1 4h2" />
        <path d="M9 13l1-3h4" />
        <path d="M14 10l2-2" />
        <path d="M10 13v-1M14 13v-1" />
      </svg>
    ),
  },
];

/** Display label lookup. Returns the key itself for any unknown
 *  string so a stale client doesn't render a blank chip if the
 *  Worker ever surfaces a key the bundled UI hasn't been deployed
 *  with yet. */
export function activityLabel(key: string): string {
  return ACTIVITIES.find((a) => a.key === key)?.label ?? key;
}
